import React, { useState, useEffect } from "react";
import { Box, Grid, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { useLogger } from "../../../../contexts/LoggerContext";
import { DefensivePlayLog, RosterPlayer } from "../../../../types/LogTypes";
import { PlayerSelection } from "../Components/PlayerSelection";
import { useDepthChart } from "../../../../contexts/DepthChartContext";
import { useRoster } from "../../../../contexts/RosterContext";
import { YardlineInput } from "../Components/YardlineInput";

export const DefenseTurnoverLog = () => {
  const {
    selectedGame,
    gameSituation,
    setGameSituation,
    currentPlayLog,
    setCurrentPlayLog,
  } = useLogger();
  const { defensePlayers } = useDepthChart();
  const { rosterPlayers } = useRoster();
  const [ballReturnedTo, setBallReturnedTo] = useState(gameSituation.yardline);
  const [turnoverType, setTurnoverType] = useState<"fumble" | "interception" | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<RosterPlayer | null>(
    null
  );
  const [jerseyNumber, setJerseyNumber] = useState<string>("");

  const players = [...defensePlayers];

  const findTakeawayPlayer = (jerseyNumber: string) => {
    return rosterPlayers.find(
      (player) => player.jersey_number?.toString() === jerseyNumber
    );
  };

  const handleTurnoverTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setTurnoverType(event.target.value as "fumble" | "interception");
    setSelectedPlayer(null);
  };

  const handleJerseyNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (value === "" || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
      setJerseyNumber(value);
      const player = findTakeawayPlayer(value);
      if (player) {
        setSelectedPlayer(player);
      }
    }
  };

  const handleTurnoverPlayerSelection = (player: RosterPlayer) => {
    setSelectedPlayer(player);
    setJerseyNumber(player.jersey_number?.toString() || "");
  };

  useEffect(() => {
    if (turnoverType === "fumble") {
      setCurrentPlayLog({
        ...currentPlayLog,
        fumble: {
          fumble_roster_player: selectedPlayer,
          fumble_club: selectedGame?.user_club,
          fumble_recovery_club: selectedGame?.opponent_club,
        },
      } as DefensivePlayLog); 
    }
    if (turnoverType === "interception") {
      setCurrentPlayLog({
        ...currentPlayLog,
        interception: {
          interception_roster_player: selectedPlayer,
          turnover_club: selectedGame?.user_club,
          takeaway_club: selectedGame?.opponent_club,
        },
      } as DefensivePlayLog);
    }
  }, [turnoverType, selectedPlayer]);

  useEffect(() => {
    setGameSituation({
      ...gameSituation,
      yardline: ballReturnedTo,
    });
  }, [ballReturnedTo]);

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Turnover Type */}
        <Grid item xs={2}>
          <RadioGroup value={turnoverType} onChange={handleTurnoverTypeChange}>
            <FormControlLabel
              value="fumble"
              control={<Radio />}
              label="Fumble"
            />
            <FormControlLabel
              value="interception"
              control={<Radio />}
              label="Interception"
            />
          </RadioGroup>
        </Grid>

        {/* Player */}
        <Grid item xs={6}>
          <PlayerSelection
            title="Takeaway By"
            players={players}
            selectedPlayer={selectedPlayer}
            jerseyNumber={jerseyNumber}
            maxDisplayedPlayers={5}
            handlePlayerSelection={handleTurnoverPlayerSelection}
            handleJerseyNumberChange={handleJerseyNumberChange}
          />
        </Grid>
        {/* Ball Returned To */}
        <Grid item xs={4}>
          <YardlineInput
            value={ballReturnedTo}
            onChange={setBallReturnedTo}
            label="Ball Returned To"
          />
        </Grid>
      </Grid>
    </Box>
  );
};
